<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Semesters Management
            </h1>
            <button @click="openModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add Semester
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by name..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Academic Year Filter -->
                    <SearchableSelect
                        v-model="filters.academic_year_id"
                        :options="academicYearOptions"
                        placeholder="All Academic Years"
                        option-label="name"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchSemesters"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.is_active"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchSemesters"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="semesterStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="7" />
        </div>

        <!-- Error State -->
        <div
            v-if="semesterStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ semesterStore.error }}
                    </p>
                    <button
                        @click="semesterStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!semesterStore.loading && !semesterStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Code
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Name
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Academic Year
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Start Date
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                End Date
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="semester in semesterStore.semesters"
                            :key="semester.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded"
                                >
                                    {{ semester.code || "-" }}
                                </div>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ semester.name }}
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ semester.academic_year?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDate(semester.start_date) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDate(semester.end_date) }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="semester.is_active"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Active
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                >
                                    Inactive
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editSemester(semester)"
                                    title="Edit Semester"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    v-if="!semester.is_active"
                                    @click="setActive(semester)"
                                    title="Set Active"
                                    class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 rounded-md hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors mr-2"
                                >
                                    <CheckCircle class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(semester)"
                                    title="Delete Semester"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="semesterStore.semesters.length === 0"
                :icon="Calendar"
                title="No semesters found"
                description="Get started by adding a new semester."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Semester
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="semesterStore.meta"
                :per-page="semesterStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Semester Modal -->
        <SemesterModal
            :show="showModal"
            :semester="selectedSemester"
            :is-editing="isEditing"
            :academic-years="academicYears"
            @close="closeModal"
            @saved="handleSave"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            :show="showDeleteModal"
            title="Delete Semester"
            :message="
                semesterToDelete
                    ? `Are you sure you want to delete ${semesterToDelete.name}? This action cannot be undone.`
                    : ''
            "
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useSemesterStore } from "@/stores/semester";
import { useAcademicYearStore } from "@/stores/academicYear";
import SearchableSelect from "@/components/SearchableSelect.vue";
import SemesterModal from "@/components/SemesterModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import NotificationService from "@/utils/notifications";
import academicYearApi from "@/api/academicYear";
import type { Semester } from "@/api/semester";
import type { AcademicYear } from "@/api/academicYear";
import {
    Plus,
    RotateCcw,
    Pencil,
    CheckCircle,
    Trash2,
    Calendar,
} from "@lucide/vue";

const semesterStore = useSemesterStore();
const academicYearStore = useAcademicYearStore();
// State
const searchQuery = ref("");
const filters = ref({
    is_active: null as boolean | null,
    academic_year_id: undefined as string | undefined,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedSemester = ref<Semester | null>(null);
const isEditing = ref(false);
const semesterToDelete = ref<Semester | null>(null);

// Computed
const academicYears = computed(() => academicYearStore.academicYears);

// Options for Academic Year filter
const academicYearOptions = computed(() => {
    const options = [
        { id: undefined as string | undefined, name: "All Academic Years" },
    ];
    academicYears.value.forEach((year: AcademicYear) => {
        options.push({ id: year.id, name: year.name });
    });
    return options;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.is_active !== null ||
        filters.value.academic_year_id !== undefined ||
        semesterStore.page !== 1 ||
        semesterStore.perPage !== 10
    );
});

// Options for Status filter
const statusOptions = [
    { value: null, label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
];

// Methods
const handlePerPageChange = (value: number) => {
    semesterStore.perPage = value;
    semesterStore.page = 1;
    fetchSemesters();
};

const fetchSemesters = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await semesterStore.fetchSemesters(searchFilters);
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = { is_active: null, academic_year_id: undefined };
    semesterStore.setPage(1);
    semesterStore.setPerPage(10);
    fetchSemesters();
};

const debouncedSearch = useDebounce(fetchSemesters, 300);

const openModal = () => {
    selectedSemester.value = null;
    isEditing.value = false;
    showModal.value = true;
    // Clear any previous errors when opening modal
    semesterStore.clearError();
};

const editSemester = (semester: Semester) => {
    selectedSemester.value = semester;
    isEditing.value = true;
    showModal.value = true;
    // Clear any previous errors when opening modal
    semesterStore.clearError();
};

const closeModal = () => {
    showModal.value = false;
    selectedSemester.value = null;
    isEditing.value = false;
};

const confirmDelete = (semester: Semester) => {
    semesterToDelete.value = semester;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    semesterToDelete.value = null;
};

const handleSave = async () => {
    const wasEditing = isEditing.value;
    closeModal();
    await semesterStore.fetchSemesters();
    showNotification(
        wasEditing
            ? "Semester updated successfully"
            : "Semester created successfully",
        "success",
    );
    semesterStore.clearError();
};

const handleDelete = async () => {
    try {
        if (semesterToDelete.value) {
            await semesterStore.deleteSemester(semesterToDelete.value.id);
            showNotification("Semester deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete semester", "error");
    }
};

const setActive = async (semester: Semester) => {
    try {
        await semesterStore.setActiveSemester(semester.id);
        showNotification("Semester set as active successfully", "success");
    } catch (error) {
        showNotification("Failed to set semester as active", "error");
    }
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
};

const goToPage = (page: number) => {
    semesterStore.setPage(page);
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(async () => {
    // Fetch academic years first for the dropdown
    await academicYearStore.fetchAcademicYears();
    // Then fetch semesters
    fetchSemesters();
});
</script>
